import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { fetchUserPositions } from "services/positions.service";
import PositionMultiSelect from "features/positions/PositionMultiSelect";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import SensitiveNumberInput from "components/sensitive-amount/SensitiveNumberInput";
import { formatDay } from "features/personnel/personnel-helpers";
import type { CurrentTerms, EmploymentTermsDraft } from "./employment-terms";

interface EmploymentTermsFieldsProps {
  companyId: string;
  /** Whose current positions prefill the position picker. */
  employeeUserId?: string;
  currentTerms?: CurrentTerms;
  draft: EmploymentTermsDraft;
  onChange: React.Dispatch<React.SetStateAction<EmploymentTermsDraft>>;
  /** For forms that record old amendments: warn that the newest amendment by date decides the positions. */
  warnAboutOldPositions?: boolean;
}

const INPUT_CLASS =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

function TermBlock({ title, onRemove, children }: { title: string; onRemove: () => void; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="p-3 bg-white border border-gray-200 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-800">{title}</p>
        <button
          type="button"
          className="text-red-600 hover:text-red-800 text-sm font-medium px-1"
          onClick={onRemove}
          title={t("remove")}
          aria-label={t("remove")}
        >
          &times;
        </button>
      </div>
      {children}
    </div>
  );
}

/**
 * The «Position / compensation change» box of the amendment forms: three
 * buttons add a position, salary or working-hours part, each added part
 * shows its own block. The state lives in the form (`draft`) so create /
 * edit, register and set-terms share this one piece.
 */
export default function EmploymentTermsFields({
  companyId,
  employeeUserId,
  currentTerms,
  draft,
  onChange,
  warnAboutOldPositions = false,
}: EmploymentTermsFieldsProps) {
  const { t } = useTranslation();

  const addPositions = async () => {
    onChange((prev) => ({ ...prev, positions: { ids: [], legacyText: "" } }));
    // Start from the employee's current positions — the amendment usually
    // adds or swaps one of them.
    if (!employeeUserId) return;
    try {
      const current = await fetchUserPositions(companyId, employeeUserId);
      onChange((prev) =>
        prev.positions && prev.positions.ids.length === 0
          ? { ...prev, positions: { ...prev.positions, ids: current.map((position) => position.id) } }
          : prev
      );
    } catch {
      // Nothing to prefill with — the picker starts empty.
    }
  };

  const addSalary = () =>
    onChange((prev) => ({
      ...prev,
      salary: currentTerms?.salary != null ? String(currentTerms.salary) : "",
    }));

  const addHours = () =>
    onChange((prev) => ({
      ...prev,
      hoursPerWeek: currentTerms?.hoursPerWeek != null ? String(currentTerms.hoursPerWeek) : "",
    }));

  const addButtons = [
    { isAdded: draft.positions !== null, label: t("add_position_update"), add: addPositions },
    { isAdded: draft.salary !== null, label: t("add_salary_update"), add: addSalary },
    { isAdded: draft.hoursPerWeek !== null, label: t("add_hours_update"), add: addHours },
  ];

  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
      <div className="mb-2">
        <p className="text-sm font-medium text-gray-800">{t("is_employment_terms_change")}</p>
        <p className="text-xs text-gray-500 mt-0.5">{t("is_employment_terms_change_hint")}</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {addButtons
          .filter((button) => !button.isAdded)
          .map((button) => (
            <button
              key={button.label}
              type="button"
              className="px-3 py-1.5 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 text-sm max-md:min-h-11"
              onClick={button.add}
            >
              + {button.label}
            </button>
          ))}
      </div>

      <div className="flex flex-col gap-3">
        {draft.positions && (
          <TermBlock
            title={t("add_position_update")}
            onRemove={() => onChange((prev) => ({ ...prev, positions: null }))}
          >
            <PositionMultiSelect
              companyId={companyId}
              value={draft.positions.ids}
              onChange={(ids: string[]) =>
                onChange((prev) => ({ ...prev, positions: prev.positions && { ...prev.positions, ids } }))
              }
              label={t("positions_new_positions")}
              required
            />
            {warnAboutOldPositions && (
              <p className="mt-2 text-xs text-gray-500">{t("amendment_positions_warning")}</p>
            )}
            {draft.positions.legacyText && draft.positions.ids.length === 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {t("positions_legacy_text_hint", { text: draft.positions.legacyText })}
              </p>
            )}
          </TermBlock>
        )}

        {draft.salary !== null && (
          <TermBlock title={t("add_salary_update")} onRemove={() => onChange((prev) => ({ ...prev, salary: null }))}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("salary")} *</label>
            <SensitiveNumberInput
              type="number"
              className={INPUT_CLASS}
              value={draft.salary}
              onChange={(event) => onChange((prev) => ({ ...prev, salary: event.target.value }))}
            />
            {currentTerms?.plannedSalaryChange && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                <Trans
                  i18nKey="amendment_cancels_planned_salary_change"
                  values={{ date: formatDay(currentTerms.plannedSalaryChange.date) }}
                  components={{ amount: <SensitiveAmount value={currentTerms.plannedSalaryChange.amount} /> }}
                />
              </p>
            )}
          </TermBlock>
        )}

        {draft.hoursPerWeek !== null && (
          <TermBlock
            title={t("add_hours_update")}
            onRemove={() => onChange((prev) => ({ ...prev, hoursPerWeek: null }))}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("hours_per_week")} *</label>
            <input
              type="number"
              className={INPUT_CLASS}
              value={draft.hoursPerWeek}
              onChange={(event) => onChange((prev) => ({ ...prev, hoursPerWeek: event.target.value }))}
            />
          </TermBlock>
        )}
      </div>
    </div>
  );
}
